// 佈署權杖（SPEC #kinio-kms-onboarding「權杖建立後」「報到進度」）：建立表單、只顯示一次的結果、家戶的權杖清單、報到進度頁。
// 報到進度的「等你確認」：預期清單外（含跟已報到的同名）的電腦先停在待確認，〔接受〕（step-up）/〔拒絕〕（SPEC #kinio-kms-enroll-review、D40；M5-05）。
// API 是 cloud/kmgr M1-09（已在 main）：建立與撤銷要 step-up（403 REAUTH_REQUIRED → 重新驗證對話框，api.ts 處理）。
// 「〈品牌〉管理程式」：畫面不出現 Agent / kmgr / 指令名（D23）。
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApp } from '../../lib/AppContext';
import { stamp } from '../../lib/timefmt';
import { useConfirm } from '../../components/ConfirmDialog';
import { SkeletonLines } from '../../components/Skeleton';
import { kmgrApi } from './api';
import { enrollReasonKey, expiryFrom, parseCidrs, parseExpectedHosts, sameHost } from './model';
import { enrollUrlFor, installParams } from './handover/model';
import { useScope } from '../../lib/scope';
import { useMarketAvailability } from '../../lib/marketGate';
import { brandName, errText, LiveStamp, NS, osText, StateBox } from './parts';
import type { CreatedDeployToken, DeployToken, Enrollment, EnrollReview, EnrollReviewResult } from './types';

const DAYS = [1, 3, 7, 14, 30];

export function tokenProgressHref(homeId: string, tokenId: string): string {
  return `/sites/kmgr/${encodeURIComponent(homeId)}/tokens/${encodeURIComponent(tokenId)}`;
}

/** 建立佈署權杖的表單（精靈第 ⑤ 步與家戶監控設定共用）。 */
export function DeployTokenForm({ api, homes, homeId, onHome, defaultName, onCreated, extraActions }: {
  api: string;
  homes: { home_id: string; label: string }[];
  homeId: string;
  onHome?: (h: string) => void;
  defaultName: string;
  onCreated(t: CreatedDeployToken): void;
  extraActions?: ReactNode;
}) {
  const { t } = useTranslation(NS);
  const [name, setName] = useState(defaultName);
  const [days, setDays] = useState(7);
  const [max, setMax] = useState('50');
  const [cidrText, setCidrText] = useState('');
  const [ownership, setOwnership] = useState<'org' | 'byod'>('org');
  const [expected, setExpected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cidrs, badCidrs] = useMemo(() => parseCidrs(cidrText), [cidrText]);
  const maxN = Number(max);
  const maxOk = Number.isInteger(maxN) && maxN >= 1 && maxN <= 10000;
  const can = !!homeId && name.trim().length > 0 && maxOk && badCidrs.length === 0 && !busy;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!can) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await kmgrApi.createDeployToken(api, homeId, { name: name.trim(), max_count: maxN, expires_at: expiryFrom(days), cidrs, ownership, expected_hosts: expected.length ? expected : undefined });
      onCreated(r);
    } catch (x) {
      setErr(errText(t, x));
    } finally {
      setBusy(false);
    }
  };
  const readFile = async (f: File | undefined) => {
    if (!f) return;
    setExpected(parseExpectedHosts(await f.text()));
  };
  return (
    <form onSubmit={(e) => void submit(e)} data-testid="km-token-form">
      <div className="km-form">
        <label className="field">
          <span>{t('token.name')}</span>
          <input className="input" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label className="field">
          <span>{t('token.home')}</span>
          <select className="input" value={homeId} disabled={!onHome} onChange={(e) => onHome?.(e.target.value)}>
            {homes.map((h) => (
              <option key={h.home_id} value={h.home_id}>
                {h.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('token.expiry')}</span>
          <select className="input" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {DAYS.map((d) => (
              <option key={d} value={d}>
                {t('token.days', { n: d, until: stamp(expiryFrom(d)).slice(5) })}
              </option>
            ))}
          </select>
          <span className="hint">{t('token.expiry_hint')}</span>
        </label>
        <label className="field">
          <span>{t('token.max')}</span>
          <input className="input tabular" inputMode="numeric" value={max} onChange={(e) => setMax(e.target.value.replace(/[^\d]/g, ''))} aria-invalid={!maxOk} aria-describedby="km-max-hint" required />
          <span id="km-max-hint" className={'hint' + (maxOk ? ' warn' : ' bad')}>
            {maxOk ? t('token.max_hint') : t('token.max_bad')}
          </span>
        </label>
        <label className="field">
          <span>{t('token.cidrs')}</span>
          <input className="input" value={cidrText} onChange={(e) => setCidrText(e.target.value)} placeholder="203.0.113.0/29" aria-invalid={badCidrs.length > 0} aria-describedby="km-cidr-hint" />
          <span id="km-cidr-hint" className={'hint' + (badCidrs.length ? ' bad' : '')}>
            {badCidrs.length ? t('token.cidrs_bad', { list: badCidrs.join('、') }) : t('token.cidrs_hint')}
          </span>
        </label>
        <label className="field">
          <span>{t('token.ownership')}</span>
          <select className="input" value={ownership} onChange={(e) => setOwnership(e.target.value as 'org' | 'byod')}>
            <option value="org">{t('token.own_org')}</option>
            <option value="byod">{t('token.own_byod')}</option>
          </select>
          <span className="hint">{t('token.ownership_hint')}</span>
        </label>
        <label className="field span-2">
          <span>{t('token.expected')}</span>
          <input className="input" type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => void readFile(e.target.files?.[0])} />
          <span className="hint">{expected.length ? t('token.expected_n', { n: expected.length }) : t('token.expected_hint')}</span>
        </label>
      </div>
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
      <div className="km-dock">
        <span className="why">{t('token.once_note')}</span>
        {extraActions}
        <button className="btn btn-primary" type="submit" disabled={!can}>
          {busy ? t('common.saving') : t('token.create')}
        </button>
      </div>
    </form>
  );
}

/** 安裝參數（SPEC #kinio-kms-onboarding「權杖建立後」的分頁；M5-03：Windows / GPO 啟動指令碼 / Intune / Linux / RMM）。 */
function InstallTabs({ token, api, agent }: { token: CreatedDeployToken; api: string; agent: string }) {
  const { t } = useTranslation(NS);
  const [tab, setTab] = useState<'windows' | 'gpo' | 'intune' | 'linux' | 'rmm'>('windows');
  const p = installParams(token.token, enrollUrlFor(api), { name: token.name, until: stamp(token.expires_at).slice(0, 10), agent });
  const gpoHref = 'data:text/plain;charset=utf-8,' + encodeURIComponent(p.gpoScript);
  const body = { windows: p.msiexec, gpo: p.gpoScript, intune: p.intune, linux: p.linux, rmm: p.rmm }[tab];
  return (
    <section className="card" data-testid="km-install-tabs" aria-labelledby="km-install-h">
      <h2 id="km-install-h" style={{ fontSize: 16, marginTop: 0 }}>
        {t('install.title')}
      </h2>
      <div className="km-seg" role="group" aria-label={t('install.title')} style={{ marginBottom: 'var(--space-2)', flexWrap: 'wrap' }}>
        {(['windows', 'gpo', 'intune', 'linux', 'rmm'] as const).map((k) => (
          <button key={k} type="button" aria-pressed={tab === k} onClick={() => setTab(k)} data-tab={k}>
            {t(`install.tab_${k}`)}
          </button>
        ))}
      </div>
      <p className="muted km-sub">{t(`install.note_${tab}`)}</p>
      <pre className="km-code" data-testid="km-install-code" data-tab={tab}>
        {body}
      </pre>
      {tab === 'gpo' && (
        <a className="btn btn-sm" href={gpoHref} download={`${token.name || 'kmgr-agent'}-gpo-startup.cmd`} data-testid="km-install-gpo-dl">
          {t('install.gpo_download')}
        </a>
      )}
    </section>
  );
}

/** 權杖只顯示一次（一次性秘密樣式，黃框）+ 裝好之後去哪裡看。 */
export function TokenOnce({ token, homeLabel, api }: { token: CreatedDeployToken; homeLabel: string; api?: string }) {
  const { t } = useTranslation(NS);
  const { config } = useApp();
  const [copied, setCopied] = useState(false);
  const agent = t('agent_name', { brand: brandName(config.display_name) });
  // 歐盟 / 英國市場閘門（SPEC #product-security-market-gate）：連線地區不提供時不給安裝參數與步驟，只留說明
  const market = useMarketAvailability(config, 'kmgr-agent');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const fileHref = useMemo(() => 'data:text/plain;charset=utf-8,' + encodeURIComponent(token.token + '\n'), [token.token]);
  return (
    <div data-testid="km-token-once">
      <section className="km-once" aria-labelledby="km-once-h">
        <h2 id="km-once-h">{t('token.once_title')}</h2>
        <p style={{ margin: 0 }}>{t('token.once_body')}</p>
        <code data-testid="km-token-value">{token.token}</code>
        <div className="toolbar wrap" style={{ margin: 0 }}>
          <button className="btn btn-primary btn-sm" onClick={() => void copy()}>
            {copied ? t('token.copied') : t('token.copy')}
          </button>
          <a className="btn btn-sm" href={fileHref} download={`${token.name || 'token'}.txt`}>
            {t('token.download')}
          </a>
          <span className="muted km-sub">{t('token.once_meta', { name: token.name, home: homeLabel, n: token.max_count, until: stamp(token.expires_at) })}</span>
        </div>
        <span className="km-sr" role="status">
          {copied ? t('token.copied') : ''}
        </span>
        {token.rebuilt_from && <p className="km-sub" data-testid="km-token-rebuilt-note">{t('token.rebuilt_note')}</p>}
      </section>
      {market.available && api && <InstallTabs token={token} api={api} agent={agent} />}
      <div className="km-wiz">
        {market.available ? (
          <section className="card">
            <h2 style={{ fontSize: 16, marginTop: 0 }}>{t('token.install_title', { agent })}</h2>
            <ol>
              <li>{t('token.install_1', { agent })}</li>
              <li>{t('token.install_2')}</li>
              <li>{t('token.install_3', { agent })}</li>
            </ol>
            <p className="muted km-sub">{t('token.install_note')}</p>
          </section>
        ) : (
          <section className="card" role="status" data-testid="market-gate-notice" data-product="kmgr-agent">
            <h2 style={{ fontSize: 16, marginTop: 0 }}>{t('market.title')}</h2>
            <p className="muted km-sub">{t('market.agent_body', { agent })}</p>
          </section>
        )}
        <section className="card">
          <h2 style={{ fontSize: 16, marginTop: 0 }}>{t('token.after_title')}</h2>
          <p>{t('token.after_body', { agent })}</p>
          <Link className="btn btn-primary" to={tokenProgressHref(token.home_id, token.token_id)}>
            {t('token.see_progress')}
          </Link>
        </section>
      </div>
    </div>
  );
}

export function TokenStatePill({ tok }: { tok: Pick<DeployToken, 'state'> }) {
  const { t } = useTranslation(NS);
  const cls = tok.state === 'active' ? 'ok' : tok.state === 'revoked' ? 'bad' : 'unset';
  const icon = tok.state === 'active' ? '✓' : tok.state === 'revoked' ? '✕' : '○';
  return (
    <span className={'km-st ' + cls} data-token-state={tok.state}>
      <span aria-hidden>{icon}</span>
      {t(`token.state_${tok.state}`)}
    </span>
  );
}

/** 家戶的權杖清單。 */
export function DeployTokenList({ api, homeId }: { api: string; homeId: string }) {
  const { t } = useTranslation(NS);
  const q = useQuery({ queryKey: ['kmgr', 'tokens', homeId], queryFn: () => kmgrApi.deployTokens(api, homeId), enabled: !!api, refetchInterval: 15_000 });
  if (q.isLoading) return <SkeletonLines n={3} label={t('common.loading')} />;
  if (q.isError)
    return (
      <StateBox icon="⊘" title={t('token.list_unavailable')} body={errText(t, q.error)}>
        <button className="btn" onClick={() => void q.refetch()}>
          {t('common.retry')}
        </button>
      </StateBox>
    );
  const list = q.data?.deploy_tokens ?? [];
  if (!list.length)
    return (
      <StateBox icon="○" title={t('token.none')} body={t('token.none_body')} testId="km-tokens-empty">
        <Link className="btn btn-primary" to={`/sites/kmgr/${encodeURIComponent(homeId)}/tokens/new`}>
          {t('token.new')}
        </Link>
      </StateBox>
    );
  return (
    <div className="card">
      <table className="km-table" data-testid="km-token-list">
        <thead>
          <tr>
            <th>{t('token.name')}</th>
            <th>{t('token.col_state')}</th>
            <th>{t('token.col_progress')}</th>
            <th>{t('token.col_expires')}</th>
          </tr>
        </thead>
        <tbody>
          {list.map((x) => (
            <tr key={x.token_id}>
              <td>
                <Link to={tokenProgressHref(homeId, x.token_id)}>{x.name}</Link>
              </td>
              <td>
                <TokenStatePill tok={x} />
              </td>
              <td className="tabular">{t('token.progress_short', { a: x.progress?.approved ?? x.used_count, n: x.max_count, f: x.progress?.failed ?? 0 })}</td>
              <td className="tabular">{stamp(x.expires_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 報到進度（權杖詳細）：已報到 / 失敗 / 還沒報到，每台一列，失敗原因白話對照錯誤碼 + 你能做什麼。 */
export function TokenProgress({ api, homeId, tokenId }: { api: string; homeId: string; tokenId: string }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const { ask, dialog } = useConfirm();
  const [err, setErr] = useState<string | null>(null);
  const [rebuilt, setRebuilt] = useState<CreatedDeployToken | null>(null);
  const { homes } = useScope();
  const tok = useQuery({ queryKey: ['kmgr', 'token', homeId, tokenId], queryFn: () => kmgrApi.deployToken(api, homeId, tokenId), enabled: !!api, refetchInterval: 5_000 });
  const ens = useQuery({ queryKey: ['kmgr', 'enrollments', homeId, tokenId], queryFn: () => kmgrApi.enrollments(api, homeId, tokenId), enabled: !!api, refetchInterval: 5_000 });
  if (tok.isLoading) return <SkeletonLines n={4} label={t('common.loading')} />;
  if (!tok.data)
    return (
      <StateBox icon="⊘" title={t('token.unavailable')} body={errText(t, tok.error)}>
        <button className="btn" onClick={() => void tok.refetch()}>
          {t('common.retry')}
        </button>
      </StateBox>
    );
  const x = tok.data;
  const p = x.progress ?? { approved: x.used_count, online: 0, failed: 0, remaining: Math.max(0, x.max_count - x.used_count) };
  const pending = p.pending ?? 0;
  const list = ens.data?.enrollments ?? [];
  const failed = latestRejected(list);
  const okRows = list.filter((e) => e.result !== 'rejected');
  const seen = list.map((e) => e.hostname ?? '').filter(Boolean);
  const notYet = (x.expected_hosts ?? []).filter((h) => !seen.some((s) => sameHost(h, s)));
  // 〔重建…〕（SPEC #kinio-kms-onboarding、#kinio-kms-handover「權杖」；M5-03）：失效 / 過期的權杖沿用原設定建新的，舊的換掉
  const rebuild = async () => {
    if (!(await ask({ title: t('token.rebuild_title', { name: x.name }), body: t('token.rebuild_body', { n: Math.max(1, p.remaining) }), action: t('token.rebuild') }))) return;
    setErr(null);
    try {
      setRebuilt(await kmgrApi.rebuildDeployToken(api, homeId, tokenId));
      void qc.invalidateQueries({ queryKey: ['kmgr'] });
    } catch (e) {
      setErr(errText(t, e));
    }
  };
  const revoke = async () => {
    if (!(await ask({ danger: true, title: t('token.revoke_title', { name: x.name }), body: t('token.revoke_body', { n: p.remaining }), action: t('token.revoke') }))) return;
    setErr(null);
    try {
      await kmgrApi.revokeDeployToken(api, homeId, tokenId);
      void qc.invalidateQueries({ queryKey: ['kmgr'] });
    } catch (e) {
      setErr(errText(t, e));
    }
  };
  if (rebuilt) return <TokenOnce token={rebuilt} api={api} homeLabel={homes.find((h) => h.home_id === homeId)?.display_name ?? homeId} />;
  return (
    <div data-testid="km-token-progress">
      <div className="km-head">
        <h1 className="page-title">{x.name}</h1>
        <TokenStatePill tok={x} />
        <span className="spacer" />
        <LiveStamp updatedAt={tok.dataUpdatedAt} stale={tok.isError} />
        {(x.state === 'expired' || (x.state === 'revoked' && x.revoked_reason !== 'rebuilt')) && (
          <button className="btn btn-primary btn-sm" onClick={() => void rebuild()} data-testid="km-token-rebuild">
            {t('token.rebuild_btn')}
          </button>
        )}
        {x.state === 'active' && (
          <button className="btn btn-danger btn-sm" onClick={() => void revoke()}>
            {t('token.revoke_btn')}
          </button>
        )}
      </div>
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
      {x.state === 'revoked' && (
        <div className="km-bar bad" role="status">
          <span className="ic" aria-hidden>
            ✕
          </span>
          <span className="txt">{t(`token.revoked_${x.revoked_reason ?? 'manual'}`, { time: x.revoked_at ? stamp(x.revoked_at) : '' })}</span>
        </div>
      )}
      {x.state === 'expired' && (
        <div className="km-bar unk" role="status">
          <span className="ic" aria-hidden>
            ○
          </span>
          <span className="txt">{t('token.expired_bar', { time: stamp(x.expires_at) })}</span>
        </div>
      )}
      <div className="km-kpis">
        <div className="card" data-kpi="approved">
          <div className="muted km-sub">{t('token.kpi_approved')}</div>
          <div className="v tabular">
            {p.approved} / {x.max_count}
          </div>
          <div className="km-progress" aria-hidden>
            <span style={{ width: `${Math.min(100, Math.round((p.approved / Math.max(1, x.max_count)) * 100))}%` }} />
          </div>
        </div>
        <div className="card" data-kpi="failed">
          <div className="muted km-sub">{t('token.kpi_failed')}</div>
          <div className="v tabular" style={p.failed ? { color: 'var(--km-bad)' } : undefined}>
            {p.failed}
          </div>
        </div>
        {pending > 0 && (
          <div className="card" data-kpi="pending">
            <div className="muted km-sub">{t('token.kpi_pending')}</div>
            <div className="v tabular" style={{ color: 'var(--km-warn)' }}>
              {pending}
            </div>
            <a className="km-sub" href="#km-review-h">
              {t('review.go')}
            </a>
          </div>
        )}
        <div className="card" data-kpi="remaining">
          <div className="muted km-sub">{t('token.kpi_remaining')}</div>
          <div className="v tabular">{p.remaining}</div>
          <div className="muted km-sub">{t('token.kpi_remaining_note')}</div>
        </div>
      </div>
      <EnrollReviews api={api} homeId={homeId} tok={x} />
      {failed.length > 0 && (
        <section className="card" style={{ marginBottom: 'var(--space-3)' }} aria-labelledby="km-failed-h">
          <h2 id="km-failed-h" style={{ fontSize: 16, marginTop: 0 }}>
            {t('token.failed_title', { n: failed.length })}
          </h2>
          <table className="km-table" data-testid="km-failed">
            <thead>
              <tr>
                <th>{t('token.col_host')}</th>
                <th>{t('token.col_reason')}</th>
              </tr>
            </thead>
            <tbody>
              {failed.map((e) => (
                <EnrollRow key={e.enrollment_id} e={e} />
              ))}
            </tbody>
          </table>
        </section>
      )}
      <section className="card" style={{ marginBottom: 'var(--space-3)' }} aria-labelledby="km-notyet-h">
        <h2 id="km-notyet-h" style={{ fontSize: 16, marginTop: 0 }}>
          {t('token.notyet_title', { n: p.remaining })}
        </h2>
        {notYet.length > 0 && <p>{t('token.notyet_expected', { list: notYet.slice(0, 20).join('、'), more: notYet.length > 20 ? t('token.and_more', { n: notYet.length - 20 }) : '' })}</p>}
        <p className="muted km-sub">{t('token.notyet_hint')}</p>
      </section>
      <section className="card" aria-labelledby="km-ok-h">
        <h2 id="km-ok-h" style={{ fontSize: 16, marginTop: 0 }}>
          {t('token.ok_title', { n: p.approved })}
        </h2>
        {okRows.length ? (
          <table className="km-table" data-testid="km-enrolled">
            <thead>
              <tr>
                <th>{t('token.col_host')}</th>
                <th>{t('token.col_os')}</th>
                <th>{t('token.col_ip')}</th>
                <th>{t('token.col_time')}</th>
              </tr>
            </thead>
            <tbody>
              {okRows.slice(0, 50).map((e) => (
                <tr key={e.enrollment_id}>
                  <td>
                    {e.hostname || e.device_ref || '—'}
                    {e.unexpected && (
                      <span className="km-hd attention" data-testid="km-unexpected">
                        {' '}
                        <span aria-hidden>! </span>
                        {t('token.unexpected')}
                      </span>
                    )}
                  </td>
                  <td>{osText(e.os) || '—'}</td>
                  <td className="tabular">{e.source_ip || '—'}</td>
                  <td className="tabular">{stamp(e.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">{t('token.ok_none')}</p>
        )}
      </section>
      {dialog}
    </div>
  );
}

/** 逐筆結果的錯誤 → 一句白話（權杖的錯誤碼照報到失敗的對照；已處理過 / 找不到另寫）。 */
function reviewErrText(t: (k: string, o?: Record<string, unknown>) => string, code: string): string {
  if (code === 'CONFLICT' || code === 'NOT_FOUND') return t('review.err_done');
  return t(`enroll.reason_${enrollReasonKey(code)}`);
}

/**
 * 等你確認（預期清單外的報到）：主機名稱不在預期清單、或跟已報到的同名的電腦先停在這裡，接受前不拿到身分、不佔台數與席次。
 * 可以逐台或勾選後批次〔接受〕〔拒絕〕；接受要再確認一次身分（step-up，api.ts 處理），拒絕不用。權杖不是有效時只列、不能處理。
 * 讀不到（舊版 kmgr 沒有這條路由）就不畫。
 */
function EnrollReviews({ api, homeId, tok }: { api: string; homeId: string; tok: DeployToken }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const { ask, dialog } = useConfirm();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const q = useQuery({ queryKey: ['kmgr', 'reviews', homeId, tok.token_id], queryFn: () => kmgrApi.enrollReviews(api, homeId, tok.token_id), enabled: !!api, refetchInterval: 5_000, retry: false });
  const all = q.data?.reviews ?? [];
  const pending = all.filter((r) => r.state === 'pending');
  const done = all.filter((r) => r.state !== 'pending');
  if (q.isError || !all.length) return null;
  const active = tok.state === 'active';
  const chosen = pending.filter((r) => sel.has(r.review_id));
  const toggle = (id: string) =>
    setSel((cur) => {
      const n = new Set(cur);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const decide = async (decision: 'accept' | 'reject', rows: EnrollReview[]) => {
    if (!rows.length) return;
    const names = rows.map((r) => r.hostname || r.source_ip || '—');
    const list = names.slice(0, 5).join('、') + (names.length > 5 ? t('token.and_more', { n: names.length - 5 }) : '');
    const okd = await ask(
      decision === 'accept'
        ? { title: t('review.accept_title', { n: rows.length }), body: t('review.accept_body', { list, n: rows.length }), action: t('review.accept') }
        : { danger: true, title: t('review.reject_title', { n: rows.length }), body: t('review.reject_body', { list }), action: t('review.reject') },
    );
    if (!okd) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await kmgrApi.decideEnrollReviews(api, homeId, tok.token_id, decision, rows.map((r) => r.review_id));
      const bad = res.results.filter((r: EnrollReviewResult) => r.error);
      const good = res.results.length - bad.length;
      const host = (id: string) => rows.find((r) => r.review_id === id)?.hostname || id;
      setMsg(
        bad.length
          ? { ok: false, text: t('review.partial', { ok: good, bad: bad.map((b) => `${host(b.review_id)}（${reviewErrText(t, b.error ?? '')}）`).join('、') }) }
          : { ok: true, text: t(decision === 'accept' ? 'review.accepted_n' : 'review.rejected_n', { n: good }) },
      );
      setSel(new Set());
      void qc.invalidateQueries({ queryKey: ['kmgr'] });
    } catch (e) {
      setMsg({ ok: false, text: errText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  const allChecked = pending.length > 0 && chosen.length === pending.length;
  return (
    <section className="card" style={{ marginBottom: 'var(--space-3)' }} aria-labelledby="km-review-h" data-testid="km-reviews">
      <h2 id="km-review-h" style={{ fontSize: 16, marginTop: 0 }}>
        {t('review.title', { n: pending.length })}
      </h2>
      <p className="muted km-sub">{t('review.intro')}</p>
      {msg && (
        <p className={msg.ok ? 'km-sub' : 'error'} role={msg.ok ? 'status' : 'alert'}>
          {msg.text}
        </p>
      )}
      {pending.length > 0 ? (
        <>
          {active && (
            <div className="toolbar wrap" style={{ margin: '0 0 var(--space-2)' }}>
              <span className="muted km-sub">{chosen.length ? t('review.selected', { n: chosen.length }) : t('review.select_hint')}</span>
              <span className="spacer" />
              <button className="btn btn-sm" disabled={!chosen.length || busy} onClick={() => void decide('reject', chosen)}>
                {t('review.reject_btn')}
              </button>
              <button className="btn btn-primary btn-sm" disabled={!chosen.length || busy} onClick={() => void decide('accept', chosen)}>
                {t('review.accept_btn')}
              </button>
            </div>
          )}
          <table className="km-table" data-testid="km-review-pending">
            <thead>
              <tr>
                {active && (
                  <th style={{ width: 32 }}>
                    <input type="checkbox" aria-label={t('review.select_all')} checked={allChecked} onChange={() => setSel(allChecked ? new Set() : new Set(pending.map((r) => r.review_id)))} />
                  </th>
                )}
                <th>{t('token.col_host')}</th>
                <th>{t('review.col_reason')}</th>
                <th>{t('token.col_ip')}</th>
                <th>{t('review.col_seen')}</th>
                {active && <th>{t('review.col_actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {pending.map((r) => (
                <tr key={r.review_id} data-review={r.review_id} data-reason={r.reason}>
                  {active && (
                    <td>
                      <input type="checkbox" aria-label={t('review.select_one', { host: r.hostname || r.source_ip || '—' })} checked={sel.has(r.review_id)} onChange={() => toggle(r.review_id)} />
                    </td>
                  )}
                  <td>
                    <div>{r.hostname || t('review.no_hostname')}</div>
                    <div className="muted km-sub">{osText(r.os) || '—'}</div>
                  </td>
                  <td>
                    <span className="km-st attention">
                      <span aria-hidden>! </span>
                      {t(`review.reason_${r.reason}`)}
                    </span>
                  </td>
                  <td className="tabular">{r.source_ip || '—'}</td>
                  <td className="tabular km-sub">
                    <div>{t('review.first', { time: stamp(r.first_at) })}</div>
                    <div className="muted">{t('review.last', { time: stamp(r.last_at) })}</div>
                  </td>
                  {active && (
                    <td>
                      <div className="toolbar" style={{ margin: 0 }}>
                        <button className="btn btn-sm" disabled={busy} onClick={() => void decide('reject', [r])}>
                          {t('review.reject_btn')}
                        </button>
                        <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => void decide('accept', [r])}>
                          {t('review.accept_btn')}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {!active && <p className="muted km-sub">{t('review.inactive')}</p>}
        </>
      ) : (
        <p className="muted">{t('review.none_pending')}</p>
      )}
      {done.length > 0 && (
        <details className="km-sub" data-testid="km-review-done">
          <summary>{t('review.done_title', { n: done.length })}</summary>
          <ul>
            {done.map((r) => (
              <li key={r.review_id} data-review-state={r.state}>
                {r.hostname || r.source_ip || '—'} · {t(`review.state_${r.state}`)}
                {r.decided_at ? ` · ${stamp(r.decided_at)}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
      {dialog}
    </section>
  );
}

/** 同一台（hw_anchor）只留最新一筆；之後成功的不算失敗。 */
function latestRejected(list: Enrollment[]): Enrollment[] {
  const byHost = new Map<string, Enrollment>();
  for (const e of [...list].sort((a, b) => Date.parse(b.at) - Date.parse(a.at))) {
    const k = e.hw_anchor_hash || e.hostname || String(e.enrollment_id);
    if (!byHost.has(k)) byHost.set(k, e);
  }
  return [...byHost.values()].filter((e) => e.result === 'rejected');
}

function EnrollRow({ e }: { e: Enrollment }) {
  const { t } = useTranslation(NS);
  const k = enrollReasonKey(e.reason);
  return (
    <tr data-reason={e.reason}>
      <td>
        <div>{e.hostname || e.source_ip || '—'}</div>
        <div className="muted km-sub">{[osText(e.os), stamp(e.at)].filter(Boolean).join(' · ')}</div>
      </td>
      <td>
        <span className="km-st bad">
          <span aria-hidden>✕</span>
          {t('token.failed')}
        </span>
        <div>
          <strong>{t(`enroll.reason_${k}`)}</strong>
        </div>
        <div className="km-sub">{t('enroll.todo_prefix') + t(`enroll.todo_${k}`)}</div>
        <details className="km-sub">
          <summary>{t('common.details')}</summary>
          <span className="muted">{[e.reason, e.source_ip].filter(Boolean).join(' · ')}</span>
        </details>
      </td>
    </tr>
  );
}
